import { UseQueryOptions, useMutation, useQuery } from 'react-query'
import queryClient from '~/data/queryClient'
import { IInspectionChecklist, ILeanInspectionChecklist } from '~/utils/types'
import {
  IGetInspectionChecklistById,
  IGetInspectionChecklistPhotoCategoryImage,
  IUpdateInspectionChecklistPhotoCategoryImage,
  createInspectionChecklist,
  deleteInspectionChecklist,
  getInspectionChecklistById,
  getInspectionChecklistImage,
  getInspectionChecklistPhotoCategoryImage,
  listInspectionChecklists,
  updateInspectionChecklist,
  updateInspectionChecklistPhotoCategoryImage,
} from './inspectionChecklistApi'

export type IUseListInspectionChecklists = {
  lean: boolean
  enabled?: boolean
}

export const useListInspectionChecklists = (props: IUseListInspectionChecklists) => {
  return useQuery({
    queryKey: ['inspection_checklists', props.lean],
    queryFn: () => listInspectionChecklists(props.lean),
    staleTime: 15 * 1000,
    enabled: props.enabled,
  })
}

export type IUseGetInspectionChecklistById = IGetInspectionChecklistById & UseQueryOptions

export const useGetInspectionChecklistById = (props: IUseGetInspectionChecklistById) => {
  return useQuery<IInspectionChecklist>({
    queryKey: ['inspection_checklist', props.inspection_checklist_id],
    queryFn: () => getInspectionChecklistById(props),
  })
}

type IUseGetInspectionChecklistPhotoCategoryImage = IGetInspectionChecklistPhotoCategoryImage & {
  enabled?: boolean
}

export const useGetInspectionChecklistPhotoCategoryImage = (
  props: IUseGetInspectionChecklistPhotoCategoryImage,
) => {
  return useQuery<string>({
    queryKey: [
      'inspection_checklist_photo_category_image',
      props.inspection_checklist_id,
      props.category_uuid,
    ],
    queryFn: () => getInspectionChecklistPhotoCategoryImage(props),
    enabled: props.enabled,
  })
}

export type IUseUpdateInspectionChecklistPhotoCategoryImage =
  IUpdateInspectionChecklistPhotoCategoryImage

export const useUpdateInspectionChecklistPhotoCategoryImage = () => {
  return useMutation({
    mutationFn: updateInspectionChecklistPhotoCategoryImage,
    onSuccess(_, props) {
      queryClient.setQueryData(
        [
          'inspection_checklist_photo_category_image',
          props.inspection_checklist_id,
          props.category_uuid,
        ],
        () => (props.image ? URL.createObjectURL(props.image) : ''),
      )
    },
  })
}

export const useCreateInspectionChecklist = () => {
  return useMutation({
    mutationFn: createInspectionChecklist,
    onSuccess(data) {
      const updateFn = (oldData: unknown) => {
        if (!oldData) return [data]
        const currentChecklists = oldData as IInspectionChecklist[]

        return currentChecklists.concat([data])
      }

      queryClient.setQueryData(['inspection_checklists', true], updateFn)
      queryClient.setQueryData(['inspection_checklists', false], updateFn)
    },
  })
}

export const useUpdateInspectionChecklist = () => {
  return useMutation({
    mutationFn: updateInspectionChecklist,
    onSuccess(data, props) {
      const updateFn = (oldData: unknown) => {
        if (!oldData) {
          queryClient.invalidateQueries('inspection_checklists', { exact: false })
          queryClient.invalidateQueries('inspection_checklist', { exact: false })
          return
        }

        queryClient.setQueryData(
          ['inspection_checklist', props.inspection_checklist_id],
          () => data,
        )

        const currentChecklists = [...(oldData as ILeanInspectionChecklist[])]

        const replaceIndex = currentChecklists.findIndex(
          (i) => i._id === props.inspection_checklist_id,
        )

        if (replaceIndex > -1) currentChecklists[replaceIndex] = data

        return currentChecklists
      }

      queryClient.setQueryData(['inspection_checklists', true], updateFn)
      queryClient.setQueryData(['inspection_checklists', false], updateFn)
    },
  })
}

export const useDeleteInspectionChecklist = () => {
  return useMutation({
    mutationFn: deleteInspectionChecklist,
    onSuccess(data, props) {
      const updateFn = (oldData: unknown) => {
        if (!oldData) return []
        const currentChecklists = oldData as IInspectionChecklist[]

        return currentChecklists.filter((i) => i._id !== props)
      }

      queryClient.setQueryData(['inspection_checklists', true], updateFn)
      queryClient.setQueryData(['inspection_checklists', false], updateFn)
    },
  })
}

export const useGetInspectionChecklistImage = (
  inspectionChecklistId?: string,
  enabled = true,
) => {
  return useQuery({
    queryKey: ['inspection_checklist_photo_url', inspectionChecklistId],
    queryFn: () => getInspectionChecklistImage(inspectionChecklistId),
    enabled,
  })
}